import { createStore } from 'vuex';
import Cookies from 'js-cookie';
import products from './products';
import { syncCartWithServer, fetchCartFromServer, convertServerCartToLocal } from '@/services/tempCart';

// Helpers to handle variant-aware cart line identity
const normalizeObject = (obj) => {
  if (!obj) return '';
  // Create a stable, sorted JSON string of the object
  const keys = Object.keys(obj).sort();
  const sorted = {};
  keys.forEach((k) => { sorted[k] = obj[k]; });
  return JSON.stringify(sorted);
};

const generateLineId = (item) => {
  const baseId = item.id ?? item.product_id ?? 'unknown';
  const blister = item.blister_id ?? '';
  const variantsSig = normalizeObject(item.variants);
  return `${baseId}::${blister}::${variantsSig}`;
};

const initCart = () => {
  try {
    const raw = JSON.parse(Cookies.get('cart') || '[]');
    return (raw || []).map((it) => ({
      ...it,
      lineId: it.lineId || generateLineId(it),
    }));
  } catch {
    return [];
  }
};

const cookieOptions = {
  expires: 7,
  sameSite: 'Lax',
  secure: process.env.NODE_ENV === 'production',
  path: '/'
};

export default createStore({
  modules: {
    products
  },
  state: {
    cart: initCart(),
    auth: {
      token: localStorage.getItem('accessToken') || null,
      username: localStorage.getItem('username') || '',
      isAdmin: localStorage.getItem('isAdmin') === 'true',
      userId: localStorage.getItem('userId') || null,
      userEmail: localStorage.getItem('userEmail') || '',
      customerType: localStorage.getItem('customerType') || 'REGULAR',
    },
    syncInProgress: false,
    lastSyncTime: null,
    syncEnabled: true,
  },
  getters: {
    cartCount: (state) => {
      return state.cart.reduce((total, item) => total + item.quantity, 0);
    },
    cartTotal: (state) => {
      return state.cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    },
    cartItems: (state) => state.cart,
    isCartOpen: (state) => state.isCartOpen,
    isSyncing: (state) => state.syncInProgress,
    lastSyncTime: (state) => state.lastSyncTime,
    
    // Auth getters
    isAuthenticated: (state) => !!state.auth.token,
    currentUsername: (state) => state.auth.username,
    isAdmin: (state) => state.auth.isAdmin,
    userId: (state) => state.auth.userId,
    userEmail: (state) => state.auth.userEmail,
    customerType: (state) => state.auth.customerType,
    isDistributor: (state) => state.auth.customerType === 'DISTRIBUTOR',
  },
  mutations: {
    // Existing cart mutations
    ADD_TO_CART(state, item) {
      const lineId = generateLineId(item);
      const existingItem = state.cart.find(i => i.lineId === lineId);
      if (existingItem) {
        existingItem.quantity += item.quantity || 1;
      } else {
        state.cart.push({
          id: item.id,
          blister_id: item.blister_id,
          name: item.name,
          price: item.price, // Ya viene con descuento aplicado desde ProductDetails
          original_price: item.original_price, // Precio original sin descuento
          brand: item.brand,
          image: item.image,
          category_id: item.category_id,
          quantity: item.quantity || 1,
          currency: item.currency || 'ARS',
          discount: item.discount,
          total_discount: item.total_discount || item.discount || 0,
          variants: item.variants || {},
          has_special_promotion: item.has_special_promotion || false,
          special_promotion_name: item.special_promotion_name || null,
          price_tier: item.price_tier || 'REGULAR',
          lineId
        });
      }
      Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
    },
    REMOVE_FROM_CART(state, lineId) {
      state.cart = state.cart.filter(item => item.lineId !== lineId);
      Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
    },
    UPDATE_QUANTITY(state, { lineId, quantity }) {
      const productIndex = state.cart.findIndex(item => item.lineId === lineId);
      if (productIndex !== -1) {
        // Verificar que quantity sea un número válido
        const validQuantity = isNaN(quantity) ? state.cart[productIndex].quantity : quantity;
        // Asegurar que la cantidad mínima sea 1
        const finalQuantity = Math.max(1, validQuantity);
        
        // Crear una copia del producto y actualizar su cantidad para garantizar reactividad
        const updatedProduct = { ...state.cart[productIndex], quantity: finalQuantity };
        
        // Actualizar el producto en el array del carrito
        state.cart = [
          ...state.cart.slice(0, productIndex),
          updatedProduct,
          ...state.cart.slice(productIndex + 1)
        ];
        
        // Solo removemos el producto si específicamente se indica una cantidad de 0
        if (quantity === 0) {
          state.cart = state.cart.filter(item => item.lineId !== lineId);
        }
      }
      // Actualizar la cookie con los datos del carrito
      Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
    },
    UPDATE_CART_ITEM_PRICE(state, { lineId, price, original_price, discount, total_discount }) {
      const idx = state.cart.findIndex(item => item.lineId === lineId);
      if (idx !== -1) {
        state.cart[idx] = {
          ...state.cart[idx],
          price,
          original_price: original_price ?? state.cart[idx].original_price,
          discount: discount ?? state.cart[idx].discount,
          total_discount: total_discount ?? state.cart[idx].total_discount,
        };
        Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
      }
    },
    CLEAR_CART(state) {
      state.cart = [];
      Cookies.remove('cart', cookieOptions);
      // Eliminar también el ID anónimo del carrito
      localStorage.removeItem('cart_anonymous_id');
    },
    // Sync status mutations
    SET_SYNC_STATUS(state, status) {
      state.syncInProgress = status;
    },
    SET_LAST_SYNC_TIME(state) {
      state.lastSyncTime = Date.now();
    },
    SET_SYNC_ENABLED(state, enabled) {
      state.syncEnabled = enabled;
    },
    // Para sincronizar el carrito local con el del servidor
    SET_CART_FROM_SERVER(state, serverCart) {
      if (serverCart && serverCart.length > 0) {
        state.cart = serverCart;
        Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
      }
    },
    
    // Auth mutations
    SET_AUTH_USER(state, { token, username, isAdmin, userId, userEmail, customerType }) {
      state.auth.token = token;
      state.auth.username = username;
      state.auth.isAdmin = isAdmin;
      state.auth.userId = userId || null;
      state.auth.userEmail = userEmail || '';
      state.auth.customerType = customerType || 'REGULAR';
      
      // Also update localStorage
      localStorage.setItem('accessToken', token);
      localStorage.setItem('username', username);
      localStorage.setItem('isAdmin', isAdmin ? 'true' : 'false');
      if (userId) localStorage.setItem('userId', userId);
      if (userEmail) localStorage.setItem('userEmail', userEmail);
      localStorage.setItem('customerType', customerType || 'REGULAR');
    },
    CLEAR_AUTH(state) {
      state.auth.token = null;
      state.auth.username = '';
      state.auth.isAdmin = false;
      state.auth.userId = null;
      state.auth.userEmail = '';
      state.auth.customerType = 'REGULAR';
      
      // Clear localStorage auth data
      localStorage.removeItem('accessToken');
      localStorage.removeItem('username');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('customerType');
    }
  },
  actions: {
    // Existing cart actions with server sync
    async addToCart({ commit, dispatch, state }, product) {
      commit('ADD_TO_CART', product);
      if (state.syncEnabled) {
        dispatch('syncCartWithServer');
      }
    },
    async removeFromCart({ commit, dispatch, state }, lineId) {
      commit('REMOVE_FROM_CART', lineId);
      if (state.syncEnabled) {
        dispatch('syncCartWithServer');
      }
    },
    async updateQuantity({ commit, dispatch, state }, payload) {
      commit('UPDATE_QUANTITY', payload);
      if (state.syncEnabled) {
        dispatch('syncCartWithServer');
      }
    },
    async clearCart({ commit, dispatch, state }) {
      commit('CLEAR_CART');
      if (state.syncEnabled) {
        dispatch('syncCartWithServer');
      }
    },
    
    // Servidor sync actions
    async syncCartWithServer({ commit, state }) {
      // Evitar sincronizaciones simultáneas
      if (state.syncInProgress) return;
      
      try {
        commit('SET_SYNC_STATUS', true);
        const syncResult = await syncCartWithServer(state.cart);
        commit('SET_LAST_SYNC_TIME');
        return syncResult;
      } catch (error) {
        console.error('Error en sincronización de carrito:', error);
      } finally {
        commit('SET_SYNC_STATUS', false);
      }
    },
    
    async fetchCartFromServer({ commit }) {
      try {
        commit('SET_SYNC_STATUS', true);
        const serverCart = await fetchCartFromServer();
        
        if (serverCart) {
          // Convertir formato del servidor al formato local
          const localCart = convertServerCartToLocal(serverCart);
          if (localCart.length > 0) {
            commit('SET_CART_FROM_SERVER', localCart);
          }
        }
      } catch (error) {
        console.error('Error al obtener carrito del servidor:', error);
      } finally {
        commit('SET_SYNC_STATUS', false);
      }
    },
    
    async mergeLocalAndServerCarts({ dispatch, state }) {
      // Verificar si hay un carrito en el localStorage
      const hasLocalCart = state.cart.length > 0;
      
      // Primero intentamos cargar el carrito del servidor
      await dispatch('fetchCartFromServer');
      
      // Si no teníamos carrito local o el servidor ya tiene un carrito,
      // no necesitamos enviar nada al servidor
      if (!hasLocalCart || (state.cart.length > 0)) {
        console.log('Usando carrito del servidor o no hay carrito local, evitando sincronización');
        return;
      }
      
      // Solo sincronizamos si no obtuvimos un carrito del servidor pero teníamos uno local
      console.log('Sincronizando carrito local con servidor');
      await dispatch('syncCartWithServer');
    },
    
    // Auth actions
    setAuthUser({ commit }, userData) {
      commit('SET_AUTH_USER', userData);
    },
    logout({ commit }) {
      commit('CLEAR_AUTH');
    }
  }
});